import React, { useState } from 'react';
import { TableCell, Tooltip, Box, Stack, IconButton } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { getFieldDisplayName, formatValueForDisplay } from 'src/helper/actualComparison';

interface ActualsComparisonTableCellProps {
  value: any;
  formatDisplayValue?: (value: any) => React.ReactNode;
  field: string;
  requestedValue?: any;
  hasChanged: boolean;
  isDeleted?: boolean;
  cellSx?: any;
  onClick?: () => void;
  iconsHidden?: boolean;
  onAcceptChange?: (newValue: any) => void;
  onHideIcons?: () => void;
}

const ActualsComparisonTableCell: React.FC<ActualsComparisonTableCellProps> = ({
  value,
  formatDisplayValue,
  field,
  requestedValue,
  hasChanged,
  isDeleted = false,
  cellSx = {},
  onClick,
  iconsHidden = false,
  onAcceptChange,
  onHideIcons
}) => {
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const displayValue = value;
  const format = formatDisplayValue || ((value: any) => formatValueForDisplay(value, field));
  const displayRequestedValue = format(requestedValue);

  const isHighlighted = hasChanged && !isDeleted;

  const baseStyle = {
    ...cellSx,
    backgroundColor: isHighlighted ? '#fff3cd' : cellSx.backgroundColor || 'inherit',
    textDecoration: isDeleted ? 'line-through' : 'none',
    opacity: isDeleted ? 0.6 : 1,
    transition: 'all 0.2s ease'
  };

  const handleCheckClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onHideIcons?.();
    if (onAcceptChange) {
      setTimeout(() => {
        onAcceptChange(requestedValue);
      }, 0);
    }
  };

  const handleCrossClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onHideIcons?.();
  };

  const tooltipContent = isHighlighted && requestedValue !== undefined ? (
    <Stack spacing={1} sx={{ color: 'white' }}>
      <Box>
        <strong>{getFieldDisplayName(field)}</strong>
      </Box>
      <Box sx={{ fontSize: '0.9em' }}>
        <strong>New:</strong> {displayRequestedValue}
      </Box>
      <Box sx={{ fontSize: '0.9em' }}>
        <strong>Original:</strong> {format(value)}
      </Box>
      {onAcceptChange && !iconsHidden && (
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, pt: 0.5 }}>
          <IconButton
            size="small"
            onClick={handleCheckClick}
            sx={{
              color: 'white',
              backgroundColor: 'rgba(76, 175, 80, 0.5)',
              '&:hover': {
                backgroundColor: 'rgba(76, 175, 80, 0.7)'
              },
              p: 0.5
            }}
            title="Accept new value"
          >
            <CheckIcon sx={{ fontSize: '18px' }} />
          </IconButton>
          <IconButton
            size="small"
            onClick={handleCrossClick}
            sx={{
              color: 'white',
              backgroundColor: 'rgba(244, 67, 54, 0.5)',
              '&:hover': {
                backgroundColor: 'rgba(244, 67, 54, 0.7)'
              },
              p: 0.5
            }}
            title="Reject change"
          >
            <CloseIcon sx={{ fontSize: '18px' }} />
          </IconButton>
        </Box>
      )}
    </Stack>
  ) : isDeleted ? (
    <Box sx={{ color: 'white' }}>Shift was deleted in history</Box>
  ) : null;

  return (
    <Tooltip
      title={tooltipContent}
      arrow
      placement="top"
      open={tooltipOpen}
      onOpen={() => setTooltipOpen(true)}
      onClose={() => setTooltipOpen(false)}
      disableInteractive={!isHighlighted}
    >
      <TableCell data-document-revision={hasChanged || isDeleted ? "changed" : undefined}
        align="center"
        sx={{
          ...baseStyle,
          cursor: onClick ? 'pointer' : 'inherit',
          padding: '4px 6px'
        }}
        onClick={onClick}
      >
        {format(displayValue)}
      </TableCell>
    </Tooltip>
  );
};

export default ActualsComparisonTableCell;
